import { useState, useRef } from "react";
import { Upload, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PROJECT_TYPES = [
  "Sofa & Couch Cushions", "RV & Camper Cushions", "Window & Bench Seats",
  "Outdoor & Patio", "Decorative Throw Pillows", "Boat & Marine Cushions", "Other",
];

const STEPS = [
  { n: "01", t: "Send your details", d: "Fill in measurements, project type and any notes below." },
  { n: "02", t: "We prepare a quote", d: "You'll receive fabric options and personalized pricing by email." },
  { n: "03", t: "Approve & we craft", d: "Once you're happy, payment is arranged and production begins." },
];

export default function CustomOrder() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", project_type: "", quantity: "",
    dimensions: "", fabric_option: "com", fabric_preference: "",
    com_item_number: "", com_source: "", details: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data]);
      } catch (err) {
        toast.error(`Could not upload ${file.name}. ${err?.response?.data?.detail || ""}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.storage_path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.dimensions || !form.details) {
      toast.error("Please complete all required (*) fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/custom-orders", { ...form, photos });
      setDone(true);
      toast.success("Quote request received!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error("Could not submit. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="custom-order-success" className="mx-auto max-w-2xl px-5 sm:px-8 py-32 text-center">
        <SEO title="Quote Requested" />
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-primary text-paper">
          <Check size={30} />
        </div>
        <h1 className="mt-8 font-serif text-4xl lg:text-5xl tracking-tight">Thank you, {form.name.split(" ")[0]}.</h1>
        <p className="mt-5 text-lg text-teal-anchor/70 leading-relaxed">
          Your custom quote request is in. We'll review your measurements and reply to
          <span className="font-medium text-teal-anchor"> {form.email} </span>
          within 1–2 business days with fabric options and pricing.
        </p>
        <p data-testid="welcome10-note" className="mt-4 inline-block rounded-sm bg-teal-primary/10 px-4 py-2 text-sm font-semibold text-teal-primary">
          First order? 10% off with code WELCOME10 — automatically applied to your first quote.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="custom-order-page">
      <SEO title="Custom Order & Quote Request" description="Request a personalized quote for handmade custom cushion covers. Share measurements, project details and optional photos — no online payment required." />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-14 lg:pt-28">
        <Reveal>
          <Eyebrow>Custom order</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] max-w-3xl">
            Request your custom quote.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-teal-anchor/70 leading-relaxed font-light">
            No online checkout — just a personal quote built around your project. Share your
            measurements and details, add photos if you have them, and we'll take it from there.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="border-t border-teal-anchor/15 pt-5">
              <span className="font-serif text-3xl text-teal-primary/40">{s.n}</span>
              <h3 className="mt-3 font-serif text-xl tracking-tight">{s.t}</h3>
              <p className="mt-2 text-sm text-teal-anchor/70 leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-28">
        <form onSubmit={submit} data-testid="custom-order-form" className="border border-teal-anchor/12 bg-white p-7 lg:p-10 space-y-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Full name *" id="co-name"><Input id="co-name" data-testid="co-name" value={form.name} onChange={set("name")} className="input-base" /></Field>
            <Field label="Email *" id="co-email"><Input id="co-email" type="email" data-testid="co-email" value={form.email} onChange={set("email")} className="input-base" /></Field>
            <Field label="Phone" id="co-phone"><Input id="co-phone" data-testid="co-phone" value={form.phone} onChange={set("phone")} className="input-base" /></Field>
            <Field label="Quantity" id="co-qty"><Input id="co-qty" data-testid="co-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 4 cushions" className="input-base" /></Field>
          </div>

          <Field label="Project type *" id="co-type">
            <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
              <SelectTrigger id="co-type" data-testid="co-project-type" className="input-base"><SelectValue placeholder="Select a project type" /></SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`co-type-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Measurements & dimensions *" id="co-dim" hint="Width × Depth × Thickness in inches, per cushion. For odd shapes, describe them here and add a photo below.">
            <Textarea id="co-dim" rows={3} data-testid="co-dimensions" value={form.dimensions} onChange={set("dimensions")} className="input-base" />
          </Field>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em]">Your material (COM) *</Label>
            <p className="mt-1 text-sm text-teal-anchor/55 leading-relaxed">
              This form is for <strong>Customer's Own Material</strong>. Ship your fabric to us, or share an item
              number and store link and we'll help you order it. To use one of our in-house fabrics, visit
              {" "}<a href="/covers" className="text-teal-primary link-underline">Custom Covers</a>.
            </p>
            <div className="mt-5 space-y-5 rounded-sm bg-paper-soft p-5">
              <p className="text-sm text-teal-anchor/70 leading-relaxed">
                You can ship your own material to us — we'll include shipping instructions in your quote.
                Or, if you've found a fabric online, share the item number and store link below and we'll help you order it.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="co-com-item" className="text-xs uppercase tracking-[0.15em]">Fabric item # / name</Label>
                  <Input id="co-com-item" data-testid="co-com-item" value={form.com_item_number} onChange={set("com_item_number")} placeholder="e.g. Sunbrella #5404" className="input-base mt-2 bg-white" />
                </div>
                <div>
                  <Label htmlFor="co-com-source" className="text-xs uppercase tracking-[0.15em]">Store / website link</Label>
                  <Input id="co-com-source" data-testid="co-com-source" value={form.com_source} onChange={set("com_source")} placeholder="e.g. fabricstore.com/..." className="input-base mt-2 bg-white" />
                </div>
              </div>
            </div>
          </div>

          <Field label="Project details *" id="co-details" hint="Tell us about the piece, its use (home, RV, boat, patio), and anything special.">
            <Textarea id="co-details" rows={4} data-testid="co-details" value={form.details} onChange={set("details")} className="input-base" />
          </Field>

          {/* Photo uploads */}
          <div>
            <Label className="text-xs uppercase tracking-[0.15em]">Photos (optional)</Label>
            <p className="mt-1 text-sm text-teal-anchor/60">Add up to a few photos of your cushions or space. Max 10MB each.</p>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="co-file-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="co-upload-btn"
              disabled={uploading}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-dashed border-teal-anchor/30 bg-paper py-8 text-sm text-teal-anchor/70 transition-colors duration-200 hover:border-teal-primary hover:text-teal-primary disabled:opacity-60"
            >
              {uploading ? <><Loader2 size={18} className="animate-spin" /> Uploading…</> : <><Upload size={18} /> Click to upload photos</>}
            </button>

            {photos.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="co-photo-previews">
                {photos.map((p) => (
                  <div key={p.storage_path} className="group relative aspect-square overflow-hidden border border-teal-anchor/12">
                    <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p.storage_path)} data-testid="co-remove-photo" className="absolute right-1 top-1 rounded-full bg-teal-anchor/80 p-1 text-paper opacity-0 transition-opacity group-hover:opacity-100">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" data-testid="co-submit" disabled={submitting || uploading} className="w-full rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit Quote Request"}
          </button>
          <p className="text-center text-xs text-teal-anchor/50">No payment is taken online. This sends a free, no-obligation quote request.</p>
        </form>
      </section>
    </div>
  );
}

function Field({ label, id, hint, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em]">{label}</Label>
      {hint && <p className="mt-1 text-sm text-teal-anchor/55 leading-relaxed">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}
